<script>
import { BAlert, BButton } from "bootstrap-vue-next";
import { fetchEventTables, saveEventTables } from "@/common/api";
import {
  getAllocatedSeats,
  getUnallocatedSeats,
  getUpdatedOrders,
  getUpdatedTables,
  processAssignments,
  setAllocatedSeats,
  setRemainingSeats,
  unassignTableFromOrder,
} from "@/common/assignments";
import EnvironmentBanner from "@/common/components/EnvironmentBanner.vue";
import OrdersTable from "@/common/components/OrdersTable.vue";
import SeatAssignmentMode from "@/common/components/SeatAssignmentMode.vue";
import SeatModal from "@/common/components/SeatModal.vue";
import TablesTable from "@/common/components/TablesTable.vue";

export default {
  name: "App",
  components: {
    BAlert,
    BButton,
    EnvironmentBanner,
    OrdersTable,
    SeatAssignmentMode,
    SeatModal,
    TablesTable,
  },
  data() {
    return {
      eventAccountCode: "",
      eventId: "",
      eventFunctionId: "",
      hash: "",
      message: "",
      migrationRequired: false,
      orderList: [],
      orders: [],
      seatModalOrder: null,
      seatModalShown: false,
      seatModalTable: "",
      showMigrationAlert: false,
      showMissingSeatCountAlert: false,
      splitOrder: false,
      tableList: [],
      tableStatuses: [],
      undoAssignmentOnClose: false,
      disableSave: true,
      userAccountCode: "",
    };
  },
  mounted() {
    this.getUrlParams();
    this.getEventData();
  },
  computed: {
    seatModalLabel() {
      const order = this.seatModalOrder;
      if (!order) {
        return "";
      }

      return (
        `Order ${order.number} on table ${this.seatModalTable} ` +
        `(${getUnallocatedSeats(order, this.seatModalTable)} of ` +
        `${order.seatCount} seats unallocated)`
      );
    },
    ordersMissingSeatCount() {
      return this.orders
        .filter((o) => !o.seatCount)
        .map((o) => o.number)
        .sort((a, b) => a - b);
    },
    seatModalSeats() {
      if (!this.seatModalOrder) {
        return 0;
      }

      return this.undoAssignmentOnClose
        ? getUnallocatedSeats(this.seatModalOrder, this.seatModalTable)
        : getAllocatedSeats(this.seatModalOrder, this.seatModalTable);
    },
  },
  methods: {
    allocateSeats(order, tableNumber) {
      if (this.splitOrder) {
        this.openSeatModal(order, tableNumber, true);

        return;
      }
      setRemainingSeats(order, tableNumber);
    },
    applySeats(seats) {
      setAllocatedSeats(this.seatModalOrder, this.seatModalTable, seats);
      this.undoAssignmentOnClose = false;
    },
    closeSeatModal() {
      if (this.undoAssignmentOnClose) {
        unassignTableFromOrder(this.seatModalOrder, this.seatModalTable);
      }
      this.seatModalOrder = null;
      this.undoAssignmentOnClose = false;
    },
    editSeats(order, tableNumber) {
      this.openSeatModal(order, tableNumber, false);
    },
    openSeatModal(order, tableNumber, isNewAssignment) {
      this.seatModalOrder = order;
      this.seatModalTable = tableNumber;
      this.undoAssignmentOnClose = isNewAssignment;
      this.seatModalShown = true;
    },
    getEventData() {
      const eventRequest = {
        EventId: this.eventId,
        EventAccountCode: this.eventAccountCode,
        EventFunctionId: this.eventFunctionId,
      };
      fetchEventTables(eventRequest)
        .then((response) => {
          this.hash = response.data.hash;
          const assignments = processAssignments(response.data.assignments);
          this.migrationRequired = response.data.assignments.migrationRequired;
          this.tableStatuses = response.data.tableStatuses;
          this.orders = assignments.orders;
          this.tableList = assignments.tables;
          this.orderList = this.orders.map((o) => o.number);
          this.showMigrationAlert = this.migrationRequired;
          this.showMissingSeatCountAlert = this.ordersMissingSeatCount.length > 0;
          this.disableSave = false;
          this.message = "Received latest changes.";
        })
        .catch((error) => {
          this.message = error.message;
          this.disableSave = true;
        });
    },
    getUrlParams() {
      const searchUrl = new URL(window.location);
      this.eventId = searchUrl.searchParams.get("eventId");
      this.eventAccountCode = searchUrl.searchParams.get("eventAccountCode");
      this.eventFunctionId = searchUrl.searchParams.get("eventFunctionId");
      this.userAccountCode = searchUrl.searchParams.get("userAccountCode");
    },
    updateEventTables() {
      this.message = "Updating orders and tables.";
      this.disableSave = true;
      const eventUpdate = {
        EventId: this.eventId,
        EventAccountCode: this.eventAccountCode,
        Hash: this.hash,
        Orders: getUpdatedOrders(this.orders, this.migrationRequired),
        Tables: getUpdatedTables(this.tableList, this.tableStatuses),
        userAccountCode: this.userAccountCode,
      };
      saveEventTables(eventUpdate)
        .then((response) => {
          this.message = response.data + " Getting latest changes.";
          this.getEventData();
        })
        .catch((error) => {
          this.message = error.message;
          this.disableSave = false;
        });
    },
  },
};
</script>

<template>
  <EnvironmentBanner />
  <div class="page-margins text-start">
    <BAlert
      v-model="showMigrationAlert"
      variant="warning"
      dismissible
      class="mt-3 mb-0"
    >
      Event required migration of seat allocation.
      This will not update until saved.
    </BAlert>
    <BAlert
      v-model="showMissingSeatCountAlert"
      variant="warning"
      dismissible
      class="mt-3 mb-0"
    >
      The following orders do not have a seat count assigned:
      {{ ordersMissingSeatCount.join(", ") }}.
    </BAlert>
    <SeatAssignmentMode v-model="splitOrder" class="mt-3" />
  </div>
  <div class="page-margins table-columns">
    <div class="column">
      <TablesTable
        :order-list="orderList"
        :orders="orders"
        :table-list="tableList"
        :table-statuses="tableStatuses"
        @assigned="allocateSeats"
        @edit="editSeats"
      />
    </div>
    <div class="column">
      <OrdersTable
        :orders="orders"
        :table-list="tableList"
        @assigned="allocateSeats"
        @edit="editSeats"
      />
    </div>
  </div>
  <div>
    <BButton variant="primary" :disabled="disableSave" @click="updateEventTables">
      Save
    </BButton>
  </div>
  <h3>{{ message }}</h3>
  <SeatModal
    v-model="seatModalShown"
    :label="seatModalLabel"
    :seats="seatModalSeats"
    @apply="applySeats"
    @hidden="closeSeatModal"
  />
</template>
<style src="@vueform/multiselect/themes/default.css"></style>
<style src="../public/site.css"></style>
